import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Copy, Check, AlertTriangle } from "lucide-react";
import { apiKeysApi, APIKey, CreateAPIKeyResponse } from "../../api/client";
import { formatDate } from "../../lib/utils";

export default function ApiKeysSettings() {
  const [showForm, setShowForm] = useState(false);
  const [newKey, setNewKey] = useState<CreateAPIKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: apiKeysApi.list,
  });

  const createMutation = useMutation({
    mutationKey: ["createApiKey"],
    mutationFn: apiKeysApi.create,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setNewKey(response);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteApiKey"],
    mutationFn: apiKeysApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    createMutation.mutate({
      name: formData.get("name") as string,
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (
      confirm(
        `Are you sure you want to delete the API key "${name}"? This action cannot be undone and will immediately revoke access.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleCopyKey = async (key: string) => {
    await navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDismissNewKey = () => {
    setNewKey(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">API Keys</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage API keys for programmatic access
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setNewKey(null);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </button>
      </div>

      {/* New Key Display */}
      {newKey && (
        <div className="mt-6 card bg-green-50 border-green-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">
                API Key Created Successfully
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Copy your API key now. You won't be able to see it again!
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded font-mono text-sm break-all">
                  {newKey.key}
                </code>
                <button
                  onClick={() => handleCopyKey(newKey.key)}
                  className="btn btn-secondary flex-shrink-0"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="mt-3">
                <button
                  onClick={handleDismissNewKey}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  I've copied my key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showForm && !newKey && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Create API Key
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="label">
                Key Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                className="input"
                placeholder="e.g., Jenkins CI, Production Deploy"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                A descriptive name to identify this key
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create API Key"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* API Keys List */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Active API Keys
        </h2>
        <div className="grid gap-4">
          {isLoading ? (
            <div className="text-gray-500">Loading...</div>
          ) : (data?.api_keys ?? []).length === 0 ? (
            <div className="card text-center py-8">
              <p className="text-gray-500">
                No API keys yet. Create your first key to enable programmatic
                access.
              </p>
            </div>
          ) : (
            (data?.api_keys ?? []).map((apiKey: APIKey) => (
              <div key={apiKey.id} className="card">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{apiKey.name}</h3>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                      <span>
                        Key:{" "}
                        <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                          {apiKey.key_prefix}...
                        </code>
                      </span>
                      {apiKey.last_used_at && (
                        <span>
                          Last used: {formatDate(apiKey.last_used_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(apiKey.id, apiKey.name)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete API key"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
                  Created {formatDate(apiKey.created_at)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="mt-8 card bg-gray-50">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Usage</h2>
        <p className="text-sm text-gray-600 mb-4">
          Include your API key in requests using one of these methods:
        </p>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-700">
              Authorization Header (recommended):
            </p>
            <code className="block mt-1 px-3 py-2 bg-white border border-gray-200 rounded font-mono text-sm text-gray-800">
              Authorization: Bearer dpo_your_api_key_here
            </code>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              X-API-Key Header:
            </p>
            <code className="block mt-1 px-3 py-2 bg-white border border-gray-200 rounded font-mono text-sm text-gray-800">
              X-API-Key: dpo_your_api_key_here
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
